/**
 * Extension of Kendo's ComboBox widget.
 *
 * Handles issue where once read only is turned off the enable state is not restored. Validation keys off of the enabled
 * state so it must be preserved.
 */
import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import virtualConstants from "common/helper/virtualConstants";
import popupUtils from "app/widgets/Popup/popupUtils";
import classes from "app/css/widgets/DFComboBox.module.less";
import stringUtil from "common/helper/stringUtil";

const DFComboBox = BaseWidget(kendo.ui.ComboBox).extend({

    options: {
        name: "DFComboBox",
        /** locale entry for the localized placeholder text **/
        placeholderEntry: false,

        clearButton: false,

        //The max length
        maxlength: null,

        height: virtualConstants.height,

        // if provided, specifies upper or lower case for the text input
        characterCase: null,

        popup: {
            collision: "fit"
        }
    },

    init: function(element, options) {
        kendo.ui.ComboBox.fn.init.call(this, element, options);
        this.wrapper.addClass(classes.DFComboBox);
        this.list.parent().addClass(classes.DFComboBoxPopup);
        this.input.on("input.DFComboBox", this._limit.bind(this));
        this.input.on("keydown.DFComboBox", this._caret.bind(this));
        this.bind("change", this._changed.bind(this));

        var name = this.element.attr("name");
        if (name == undefined) {
            console.error("DFComboBox widget requires a name.");
        }

        this._renderPlaceholder();
        this._renderMaxLength();
        this.ariaRequired(this.element.is("[required]"));

        if (this.options.characterCase) {
            if (this.options.characterCase === "UPPER") {
                this.input.css("text-transform", "uppercase");
            } else if (this.options.characterCase === "LOWER") {
                this.input.css("text-transform", "lowercase");
            }
        }

        //Workaround for kendo bug. Name attribute on the generated input was preventing combo boxes from working in editable grids
        //see Mantis #45100
        this.input.attr("name", "");

        this.bind("open", this._handleOpen)
    },

    destroy() {
        this.input.off(".DFComboBox");
        kendo.ui.ComboBox.fn.destroy.apply(this, arguments);
    },

    getARIAElements: function() {
        return this.input;

    },

    ariaRequired(required) {
        this.input.attr("aria-required", required ? "true" : null);
    },

    _renderPlaceholder: function() {
        if (this._readonlyCache) {
            this.input.attr("placeholder", "");
        } else if (this.options.placeholderEntry) {
            this.input.attr("placeholder", locale.get(this.options.placeholderEntry));
        }
    },

    _renderMaxLength: function() {
        this.options.maxlength = this.options.maxlength || this.input.attr("maxlength");
        this.input.removeAttr("maxlength");
    },

    _limit() {
        const val = this.input.val();
        if(!this.options.maxlength || stringUtil.length(val) <= this.options.maxlength) {
            this._currentValue = val;
        } else {
            this.input.val(this._currentValue);
            if(this._lastCaret) {
                kendo.caret(this.input, this._lastCaret[0], this._lastCaret[1]);
            }
        }
        delete this._lastCaret;
    },

    _caret() {
        this._lastCaret = kendo.caret(this.input);
    },

    _changed() {
        this._currentValue = this.value();
    },

    value(val) {
        if(arguments.length) {
            this._currentValue = val;
        }
        return kendo.ui.ComboBox.fn.value.apply(this, arguments);
    },

    _readonlyCache: false,
    _enableCache: true,

    readonly: function(readonly) {
        this._readonlyCache = (readonly === undefined ? true : readonly);
        this.wrapper.toggleClass("readonly", this._readonlyCache);
        this._editable({
            readonly: this._readonlyCache,
            disable: !this._enableCache
        });
        if (this._readonlyCache) {
            this.input.attr("tabindex", "-1");
        } else {
            this.input.removeAttr("tabindex");
        }
        this._renderPlaceholder();
    },

    enable: function(enable) {
        this._enableCache = (enable === undefined ? true : enable);
        this._editable({
            readonly: this._readonlyCache,
            disable: !this._enableCache
        });
    },

    _inSearch: false,

    _searchByWord: function() {
        this._inSearch = true;
        const res = kendo.ui.ComboBox.fn._searchByWord.apply(this, arguments);
        this._inSearch = false;
        return res;
    },

    open: function(e) {
        const flip = popupUtils.configurePopup(this.wrapper[0], this.popup, virtualConstants.height);
        if (!this._inSearch || flip) {
            return kendo.ui.ComboBox.fn.open.apply(this, arguments);
        }
    }
});

kendo.ui.plugin(DFComboBox);
export default DFComboBox;
